import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Download } from "lucide-react";

import { navSections, person } from "../data/profile";
import { subscribeScroll, scrollState } from "../three/scrollStore";
import { cn } from "../lib/utils";
import { useResumeUrl } from "../lib/resumeStore";
import portrait from "../assets/ss3.jpg";

const PAGE_LINKS = [
  { label: "Services", to: "/services" },
  { label: "All work", to: "/projects" },
  { label: "Blog", to: "/blogs" },
  { label: "Contact", to: "/contact" },
];

/**
 * Progress rail — a hairline at the very top of the viewport plus, on
 * desktop, a vertical section index down the right edge. Both read from
 * the scroll store via requestAnimationFrame rather than React state.
 */
function ProgressRail() {
  const bar = useRef(null);

  useEffect(() => {
    let raf;
    const loop = () => {
      if (bar.current) {
        bar.current.style.transform = `scaleX(${scrollState.smooth.toFixed(4)})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[70] h-[2px] bg-white/[0.04]"
    >
      <div
        ref={bar}
        className="h-full origin-left bg-brand-gradient"
        style={{ transform: "scaleX(0)" }}
      />
    </div>
  );
}

/** Right-edge section index. Desktop only — it needs the gutter. */
function SectionIndex({ active, onJump }) {
  return (
    <nav
      aria-label="Section navigation"
      className="fixed right-5 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-end gap-1 xl:flex"
    >
      {navSections.map((s, i) => {
        const on = i === active;
        return (
          <button
            key={s.id}
            onClick={() => onJump(s.id)}
            className="group flex items-center gap-2.5 py-1.5"
            aria-current={on ? "true" : undefined}
          >
            <span
              className={cn(
                "font-mono text-[0.6rem] uppercase tracking-wider2 transition-all duration-300",
                on
                  ? "text-brand-300 opacity-100"
                  : "text-slate-600 opacity-0 group-hover:opacity-100"
              )}
            >
              {s.label}
            </span>
            <span
              className={cn(
                "block h-px transition-all duration-500 ease-out2",
                on
                  ? "w-8 bg-brand-400 shadow-glow"
                  : "w-3.5 bg-white/20 group-hover:w-6 group-hover:bg-white/40"
              )}
            />
          </button>
        );
      })}
    </nav>
  );
}

export default function Nav() {
  const resumeUrl = useResumeUrl();
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const [active, setActive] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const onHome = location.pathname === "/";

  useEffect(() => {
    // Coarse subscription: fires only when progress or section changes
    // meaningfully, so this component re-renders a handful of times a page.
    const unsub = subscribeScroll((s) => {
      setActive(s.section);
      setSolid(s.y > 40);
    });
    return unsub;
  }, []);

  // Lock body scroll behind the mobile sheet.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => setOpen(false), [location.pathname]);

  const jump = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <ProgressRail />
      {onHome && <SectionIndex active={active} onJump={jump} />}

      <header
        className={cn(
          "fixed inset-x-0 top-0 z-[65] transition-all duration-500 ease-out2",
          solid
            ? "border-b border-white/[0.06] bg-ink-950/70 backdrop-blur-xl"
            : "border-b border-transparent"
        )}
      >
        <div className="shell flex h-16 items-center justify-between gap-3 sm:h-[4.5rem]">
          {/* Wordmark */}
          <Link
            to="/"
            onClick={() => onHome && jump("hero")}
            className="group flex items-center gap-2.5"
            aria-label={`${person.name} — home`}
          >
            <span className="relative block h-9 w-9 shrink-0">
              {/* Gradient ring, drawn behind the photo rather than as a
                  border, so it can glow on hover without shifting layout. */}
              <span className="absolute -inset-[2px] rounded-full bg-brand-gradient opacity-60 blur-[2px] transition-opacity duration-500 group-hover:opacity-100" />
              <img
                src={portrait}
                alt="Soham Dang"
                width="36"
                height="36"
                loading="eager"
                decoding="async"
                className="relative h-9 w-9 rounded-full object-cover ring-1 ring-ink-950"
              />
            </span>
            <span className="hidden leading-tight sm:block">
              <span className="block font-display text-sm font-semibold text-slate-100">
                {person.name}
              </span>
              <span className="block whitespace-nowrap font-mono text-[0.58rem] uppercase tracking-wider2 text-slate-500">
                Full Stack · Tech Lead
              </span>
            </span>
          </Link>

          {/* Desktop links */}
          <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary">
            {navSections.slice(1, 8).map((s, i) => (
              <button
                key={s.id}
                onClick={() => jump(s.id)}
                className={cn(
                  "relative whitespace-nowrap rounded-full px-3 py-2 text-[0.78rem] transition-colors duration-300",
                  onHome && active === i + 1
                    ? "text-slate-50"
                    : "text-slate-400 hover:text-slate-100"
                )}
              >
                {onHome && active === i + 1 && (
                  <motion.span
                    layoutId="nav-pill"
                    className="absolute inset-0 rounded-full border border-white/[0.08] bg-white/[0.05]"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <span className="relative">{s.label}</span>
              </button>
            ))}
          </nav>

          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Pages">
            {PAGE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "whitespace-nowrap rounded-full px-3 py-2 text-[0.78rem] transition-colors duration-300",
                  location.pathname.startsWith(l.to)
                    ? "text-brand-300"
                    : "text-slate-400 hover:text-slate-100"
                )}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <a
              href={resumeUrl}
              download="Soham-Dang-Resume.pdf"
              className="hidden shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-2 text-[0.78rem] text-slate-300 transition-colors hover:border-brand-400/40 hover:text-white sm:inline-flex"
            >
              <Download className="h-3.5 w-3.5" />
              Résumé
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                if (onHome) {
                  e.preventDefault();
                  jump("contact");
                }
              }}
              className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-gradient px-4 py-2 text-[0.78rem] font-semibold text-ink-950 transition-transform duration-300 hover:scale-[1.03] md:inline-flex"
            >
              Hire me
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>

            <button
              onClick={() => setOpen((v) => !v)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-slate-200 xl:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile sheet */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[64] lg:hidden"
          >
            <div
              className="absolute inset-0 bg-ink-950/92 backdrop-blur-xl"
              onClick={() => setOpen(false)}
            />
            <motion.nav
              aria-label="Mobile"
              initial={{ y: -18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -18, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex h-full flex-col overflow-y-auto px-[var(--shell-x)] pb-10 pt-24"
            >
              <ul className="space-y-1">
                {navSections.slice(1).map((s, i) => (
                  <motion.li
                    key={s.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 * i, duration: 0.35 }}
                  >
                    <button
                      onClick={() => jump(s.id)}
                      className="flex w-full items-baseline gap-4 border-b border-white/[0.06] py-4 text-left"
                    >
                      <span className="font-mono text-[0.62rem] text-slate-600">
                        {s.short}
                      </span>
                      <span className="font-display text-2xl font-medium text-slate-100">
                        {s.label}
                      </span>
                    </button>
                  </motion.li>
                ))}
                {PAGE_LINKS.map((l, i) => (
                  <motion.li
                    key={l.to}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 * (navSections.length + i), duration: 0.35 }}
                  >
                    <Link
                      to={l.to}
                      className="flex items-baseline gap-4 border-b border-white/[0.06] py-4"
                    >
                      <span className="font-mono text-[0.62rem] text-slate-600">
                        ↗
                      </span>
                      <span className="font-display text-2xl font-medium text-brand-300">
                        {l.label}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-auto pt-10">
                <a
                  href={`mailto:${person.email}`}
                  className="block font-mono text-sm text-brand-300"
                >
                  {person.email}
                </a>
                <a
                  href={resumeUrl}
                  download="Soham-Dang-Resume.pdf"
                  className="btn-primary mt-6 w-full"
                >
                  <Download className="h-4 w-4" />
                  Download résumé
                </a>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
